// Question builder for survey templates and surveys: add, edit, reorder, duplicate and delete
// questions; options, scales, required, and "show only if" conditions on an earlier question.
import React, { useState } from 'react';
import {
  Box, Stack, Paper, Typography, TextField, MenuItem, IconButton, Tooltip, Switch, FormControlLabel, Button, Chip, Menu, Grid,
} from '@mui/material';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';

const CHOICE = ['single', 'multiple', 'dropdown'];
const newId = (type) => `${type}_${Math.random().toString(36).slice(2, 6)}`;
const blank = (type) => ({
  id: newId(type), type, label: '', required: type !== 'section',
  ...(CHOICE.includes(type) ? { options: ['Option 1', 'Option 2'] } : {}),
  ...(type === 'scale' ? { min: 1, max: 10 } : {}),
});

function OptionsEditor({ options, onChange }) {
  const [draft, setDraft] = useState('');
  const add = () => { const v = draft.trim(); if (v && !options.includes(v)) onChange([...options, v]); setDraft(''); };
  return (
    <Box>
      <Stack spacing={0.75}>
        {options.map((o, i) => (
          <Stack key={i} direction="row" spacing={1} alignItems="center">
            <TextField size="small" fullWidth value={o} onChange={e => onChange(options.map((x, j) => (j === i ? e.target.value : x)))} />
            <IconButton size="small" disabled={i === 0} onClick={() => { const n = [...options]; [n[i - 1], n[i]] = [n[i], n[i - 1]]; onChange(n); }}><ArrowUpwardIcon fontSize="inherit" /></IconButton>
            <IconButton size="small" onClick={() => onChange(options.filter((_, j) => j !== i))}><DeleteIcon fontSize="inherit" /></IconButton>
          </Stack>
        ))}
      </Stack>
      <Stack direction="row" spacing={1} mt={1}>
        <TextField size="small" placeholder="Add an option" value={draft} onChange={e => setDraft(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); add(); } }} />
        <Button size="small" onClick={add}>Add</Button>
      </Stack>
    </Box>
  );
}

function ConditionEditor({ q, earlier, operators, onChange }) {
  const cond = q.condition || null;
  const dep = earlier.find(x => x.id === cond?.questionId);
  const valueOptions = dep ? (CHOICE.includes(dep.type) ? dep.options : dep.type === 'yesno' ? ['yes', 'no'] : null) : null;
  if (!earlier.length) return null;
  return (
    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
      <FormControlLabel control={<Switch size="small" checked={Boolean(cond)} onChange={e => onChange(e.target.checked ? { questionId: earlier[earlier.length - 1].id, operator: 'equals', value: '' } : null)} />}
        label={<Typography variant="body2">Show only if</Typography>} />
      {cond && (
        <>
          <TextField select size="small" value={cond.questionId} onChange={e => onChange({ ...cond, questionId: e.target.value, value: '' })} sx={{ minWidth: 220, maxWidth: 320 }}>
            {earlier.map(x => <MenuItem key={x.id} value={x.id}>{x.label || x.id}</MenuItem>)}
          </TextField>
          <TextField select size="small" value={cond.operator} onChange={e => onChange({ ...cond, operator: e.target.value })} sx={{ minWidth: 140 }}>
            {operators.map(o => <MenuItem key={o.key} value={o.key}>{o.label}</MenuItem>)}
          </TextField>
          {cond.operator !== 'answered' && (valueOptions ? (
            <TextField select size="small" value={cond.value ?? ''} onChange={e => onChange({ ...cond, value: e.target.value })} sx={{ minWidth: 180 }}>
              {valueOptions.map(o => <MenuItem key={o} value={o}>{o}</MenuItem>)}
            </TextField>
          ) : (
            <TextField size="small" value={cond.value ?? ''} onChange={e => onChange({ ...cond, value: e.target.value })} sx={{ width: 160 }} placeholder="value" />
          ))}
        </>
      )}
    </Stack>
  );
}

export default function SurveyQuestionBuilder({ questions, onChange, types, operators }) {
  const [menu, setMenu] = useState(null);
  const typeLabel = Object.fromEntries((types || []).map(t => [t.key, t.label]));
  const update = (i, patch) => onChange(questions.map((q, j) => (j === i ? { ...q, ...patch } : q)));
  const move = (i, d) => { const n = [...questions]; const j = i + d; if (j < 0 || j >= n.length) return; [n[i], n[j]] = [n[j], n[i]]; onChange(n); };
  // Removing a question also removes conditions that depended on it
  const remove = (i) => { const gone = questions[i].id; onChange(questions.filter((_, j) => j !== i).map(q => (q.condition?.questionId === gone ? { ...q, condition: undefined } : q))); };
  const dup = (i) => { const n = [...questions]; n.splice(i + 1, 0, { ...questions[i], id: newId(questions[i].type), label: `${questions[i].label} (copy)` }); onChange(n); };
  let num = 0;

  return (
    <Box>
      <Stack spacing={1.5}>
        {questions.map((q, i) => {
          if (q.type !== 'section') num += 1;
          const earlier = questions.slice(0, i).filter(x => x.type !== 'section');
          return (
            <Paper key={q.id} variant="outlined" sx={{ p: 2, borderRadius: 2, bgcolor: q.type === 'section' ? '#faf5fc' : 'background.paper' }}>
              <Stack direction="row" spacing={1} alignItems="center" mb={1.5}>
                <Chip size="small" label={q.type === 'section' ? 'Section' : `Q${num}`} color={q.type === 'section' ? 'secondary' : 'primary'} />
                <TextField select size="small" value={q.type} sx={{ minWidth: 220 }}
                  onChange={e => { const t = e.target.value; update(i, { ...blank(t), id: q.id, label: q.label, help: q.help, condition: q.condition }); }}>
                  {(types || []).map(t => <MenuItem key={t.key} value={t.key}>{t.label}</MenuItem>)}
                </TextField>
                <Box sx={{ flex: 1 }} />
                {q.type !== 'section' && (
                  <FormControlLabel control={<Switch size="small" checked={Boolean(q.required)} onChange={e => update(i, { required: e.target.checked })} />} label={<Typography variant="body2">Required</Typography>} />
                )}
                <Tooltip title="Move up"><span><IconButton size="small" disabled={i === 0} onClick={() => move(i, -1)}><ArrowUpwardIcon fontSize="small" /></IconButton></span></Tooltip>
                <Tooltip title="Move down"><span><IconButton size="small" disabled={i === questions.length - 1} onClick={() => move(i, 1)}><ArrowDownwardIcon fontSize="small" /></IconButton></span></Tooltip>
                <Tooltip title="Duplicate"><IconButton size="small" onClick={() => dup(i)}><ContentCopyIcon fontSize="small" /></IconButton></Tooltip>
                <Tooltip title="Delete"><IconButton size="small" onClick={() => remove(i)}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
              </Stack>
              <Grid container spacing={1.5}>
                <Grid item xs={12}>
                  <TextField size="small" fullWidth label={q.type === 'section' ? 'Section title' : 'Question'} value={q.label} onChange={e => update(i, { label: e.target.value })} error={!q.label} />
                </Grid>
                <Grid item xs={12}>
                  <TextField size="small" fullWidth label="Help text (optional)" value={q.help || ''} onChange={e => update(i, { help: e.target.value })} />
                </Grid>
                {CHOICE.includes(q.type) && (
                  <Grid item xs={12} md={8}>
                    <Typography variant="caption" color="text.secondary">Options</Typography>
                    <OptionsEditor options={q.options || []} onChange={options => update(i, { options })} />
                  </Grid>
                )}
                {q.type === 'multiple' && (
                  <Grid item xs={6} md={4}>
                    <TextField size="small" type="number" label="Most choices (optional)" value={q.maxChoices || ''} onChange={e => update(i, { maxChoices: e.target.value ? Number(e.target.value) : undefined })} fullWidth />
                  </Grid>
                )}
                {q.type === 'scale' && (
                  <>
                    <Grid item xs={6} sm={3}><TextField size="small" type="number" label="From" value={q.min ?? 1} onChange={e => update(i, { min: Number(e.target.value) })} inputProps={{ min: 0, max: 9 }} fullWidth /></Grid>
                    <Grid item xs={6} sm={3}><TextField size="small" type="number" label="To" value={q.max ?? 10} onChange={e => update(i, { max: Number(e.target.value) })} inputProps={{ min: 1, max: 10 }} fullWidth /></Grid>
                  </>
                )}
                {['scale', 'nps'].includes(q.type) && (
                  <>
                    <Grid item xs={6} sm={3}><TextField size="small" label="Low end label" value={q.scaleLabels?.low || ''} onChange={e => update(i, { scaleLabels: { ...(q.scaleLabels || {}), low: e.target.value } })} fullWidth /></Grid>
                    <Grid item xs={6} sm={3}><TextField size="small" label="High end label" value={q.scaleLabels?.high || ''} onChange={e => update(i, { scaleLabels: { ...(q.scaleLabels || {}), high: e.target.value } })} fullWidth /></Grid>
                  </>
                )}
                {['short_text', 'long_text'].includes(q.type) && (
                  <Grid item xs={12}><TextField size="small" fullWidth label="Placeholder (optional)" value={q.placeholder || ''} onChange={e => update(i, { placeholder: e.target.value })} /></Grid>
                )}
                <Grid item xs={12}>
                  <ConditionEditor q={q} earlier={earlier} operators={operators || []} onChange={condition => update(i, { condition: condition || undefined })} />
                </Grid>
              </Grid>
              {q.type !== 'section' && <Typography variant="caption" color="text.secondary">{typeLabel[q.type]}</Typography>}
            </Paper>
          );
        })}
        {!questions.length && <Typography color="text.secondary">No questions yet. Add the first one.</Typography>}
      </Stack>
      <Button startIcon={<AddIcon />} variant="outlined" sx={{ mt: 2 }} onClick={e => setMenu(e.currentTarget)}>Add question</Button>
      <Menu anchorEl={menu} open={Boolean(menu)} onClose={() => setMenu(null)}>
        {(types || []).map(t => <MenuItem key={t.key} onClick={() => { onChange([...questions, blank(t.key)]); setMenu(null); }}>{t.label}</MenuItem>)}
      </Menu>
    </Box>
  );
}
